/**
 * Bulk Invoice Create from File — RM Inward Without Invoice (Jul 2026).
 *
 * Second entry point to the existing bulk-consolidation flow. Instead of
 * ticking per-row checkboxes across filtered pages, the user uploads an
 * .xlsx/.csv listing entry IDs; we parse client-side, dedup, format-check,
 * and post the ID array to the existing
 *   POST /api/rm-inward/entries/aggregate-for-invoice
 * endpoint — same contract, same validation, same downstream handoff.
 * On 2xx we fire the exact `onStartCreateInvoice` callback the checkbox
 * path uses, so the "Inward with invoice" create-bill dialog opens with
 * vendor / branch / RM / quantity locked identically to the checkbox
 * flow.
 *
 * No backend endpoint added. The file is never uploaded to the server —
 * we only send the extracted ID array to the aggregation endpoint.
 * Frontend-only fix per the ticket's "reuse existing, don't rework"
 * constraint.
 */
import React, { useRef, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { Upload, Download, Loader2, AlertTriangle, FileText } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Entry-ID shape enforced by the backend generator. Counter is
// zero-padded to a MINIMUM of 5 digits (`:05d`) — so once a month's
// counter rolls past 99999 the trailing block widens to 6+ digits.
// Regex intentionally matches `\d{5,}`, not exactly `\d{5}`, so a
// valid post-99999-month ID is never flagged as malformed here.
// Actual existence + status checks are the backend's authority.
const ENTRY_ID_RE = /^RMNE_\d{6}_\d{5,}$/i;

export default function BulkInvoiceCreateFromFileDialog({
  open, onClose, onStartCreateInvoice,
}) {
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // `alertPayload` is the friendlier surface for backend 4xx detail
  // strings and client-side pre-flight failures. Keeps the dialog
  // open so the user can inspect the report + reupload.
  const [alertPayload, setAlertPayload] = useState(null);
  // Per-row malformed ID list from the client pre-flight, rendered
  // as a scrollable diagnostic block above the alert.
  const [malformedRows, setMalformedRows] = useState([]);

  const reset = () => {
    setFileName("");
    setAlertPayload(null);
    setMalformedRows([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const downloadTemplate = () => {
    // One-column .xlsx template with a header row and a sample ID.
    // Matches the exact column name the parser looks for.
    const rows = [
      ["entry_id"],
      ["RMNE_202607_00001"],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 24 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "entries");
    XLSX.writeFile(wb, "bulk_invoice_entry_ids_template.xlsx");
  };

  const extractIdsFromSheet = (buf) => {
    // Read the first sheet. Header row = first row that contains a
    // cell whose text (case-insensitive, trimmed) equals `entry_id`.
    // This is more forgiving than assuming row 1 — operators often
    // add title / metadata rows above the header.
    const wb = XLSX.read(buf, { type: "array" });
    const sheetName = wb.SheetNames[0];
    if (!sheetName) throw new Error("File contains no sheets.");
    const ws = wb.Sheets[sheetName];
    const grid = XLSX.utils.sheet_to_json(ws, {
      // Jul 2026 (diagnostic row-number fidelity) — keep `blankrows: true`
      // so array indices match physical worksheet row numbers; force
      // `range: 0` so the walk starts from spreadsheet row 1 regardless
      // of the sheet's !ref start (some CSVs pin !ref to A2 when row 1
      // is blank, which would offset every `r + 1` diagnostic row
      // report by the count of leading blanks).
      header: 1, blankrows: true, defval: "", range: 0,
    });
    if (!grid.length) throw new Error("File is empty.");

    let headerIdx = -1;
    let colIdx = -1;
    for (let r = 0; r < Math.min(grid.length, 20); r++) {
      const row = grid[r] || [];
      for (let c = 0; c < row.length; c++) {
        const cell = String(row[c] ?? "").trim().toLowerCase();
        if (cell === "entry_id" || cell === "entry id") {
          headerIdx = r; colIdx = c; break;
        }
      }
      if (headerIdx !== -1) break;
    }
    if (headerIdx === -1) {
      throw new Error(
        "No 'entry_id' column found in the uploaded file. Download the " +
        "template to see the expected shape."
      );
    }

    // Collect every non-blank cell in that column beneath the header.
    // Track original spreadsheet row numbers (1-indexed, header + 1)
    // so error reports can point the user at the exact row to fix.
    //
    // Jul 2026 (reject-whole-file diagnostic completeness) — order of
    // operations matters. Validate each row's format FIRST, then dedup
    // for the POST payload only. If we dedup-by-value first (as the
    // original write did), a malformed value repeated on multiple
    // spreadsheet rows collapses to a single diagnostic entry — under
    // reject-whole-file semantics that hides real rows the operator
    // needs to fix.
    const seenValid = new Set();
    const ordered = [];
    const malformed = [];
    for (let r = headerIdx + 1; r < grid.length; r++) {
      const cell = String((grid[r] || [])[colIdx] ?? "").trim();
      if (!cell) continue;
      const norm = cell.toUpperCase();
      if (!ENTRY_ID_RE.test(norm)) {
        // Every physical row with a malformed value is reported —
        // repeats included — so the operator sees the full extent of
        // the problem before re-uploading.
        malformed.push({ row: r + 1, value: cell });
        continue;
      }
      if (seenValid.has(norm)) continue; // AC7 — valid duplicates counted once.
      seenValid.add(norm);
      ordered.push(norm);
    }
    return { ids: ordered, malformed };
  };

  const handleFile = async (file) => {
    setAlertPayload(null);
    setMalformedRows([]);
    setFileName(file.name);
    setParsing(true);
    try {
      const buf = await file.arrayBuffer();
      const { ids, malformed } = extractIdsFromSheet(buf);
      setMalformedRows(malformed);

      // Jul 2026 (reject-whole-file guarantee) — ANY malformed row
      // rejects the entire upload. Previous behaviour silently dropped
      // malformed rows and posted the valid ones, which risked leaving
      // an intended entry uninvoiced whenever an operator's file had a
      // typo. Matches how mixed-vendor / mixed-branch / non-SUBMITTED /
      // unknown-ID files are already handled — no partial consolidation
      // ever leaves the client without the user having fixed the file
      // first.
      if (malformed.length > 0) {
        setAlertPayload({
          title: `Upload rejected — ${malformed.length} malformed entry ID${malformed.length === 1 ? "" : "s"}.`,
          detail:
            "The whole file was rejected. Fix the malformed rows shown " +
            "below (or above, if there are more than 50) and re-upload. " +
            "No entries have been consolidated.",
        });
        return;
      }

      if (ids.length === 0) {
        setAlertPayload({
          title: "File contains no entry IDs.",
          detail:
            "The file has an `entry_id` column but every row beneath the " +
            "header is blank. Add at least one entry ID and re-upload.",
        });
        return;
      }
      // POST the deduped array to the same endpoint the checkbox flow
      // uses. Backend is the single authority on existence / status /
      // vendor / branch alignment.
      setSubmitting(true);
      let data;
      try {
        const resp = await axios.post(
          `${API}/rm-inward/entries/aggregate-for-invoice`,
          { entry_ids: ids },
        );
        data = resp.data;
      } catch (e) {
        const d = e.response?.data?.detail;
        setAlertPayload({
          title: `Upload rejected — ${e.response?.status || "error"}.`,
          detail: typeof d === "string"
            ? d
            : "Failed to aggregate entries. Try again after fixing the file.",
        });
        return;
      } finally {
        setSubmitting(false);
      }
      // Success — hand off to the existing "Inward with invoice" flow.
      if (typeof onStartCreateInvoice === "function") {
        onStartCreateInvoice(data);
        toast.info(
          `Consolidated ${ids.length} entries into one invoice draft. ` +
          "Review tax / freight / discount on the with-invoice tab, then Save.",
          { duration: 9000 }
        );
        handleClose();
      }
    } catch (e) {
      setAlertPayload({
        title: "Could not read the file.",
        detail: e?.message || "Unknown parser error.",
      });
    } finally {
      setParsing(false);
    }
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Basic MIME/extension gate — the .xlsx / .csv library still does
    // the real parsing, this just short-circuits obviously-wrong types.
    const ok = /\.(xlsx|xls|csv)$/i.test(file.name);
    if (!ok) {
      toast.error("Please upload an .xlsx, .xls, or .csv file.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    handleFile(file);
  };

  const busy = parsing || submitting;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => { if (!next) handleClose(); }}
    >
      <DialogContent className="max-w-2xl" data-testid="bulk-file-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-sky-600" />
            Bulk Invoice Create from File
          </DialogTitle>
          <DialogDescription>
            Upload an .xlsx or .csv listing no-invoice entry IDs (column
            header <code className="font-mono">entry_id</code>). All entries
            must belong to the same vendor and branch and be in SUBMITTED
            status — the whole file is rejected otherwise. Nothing is
            created until you save the consolidated draft on the next tab.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              onClick={downloadTemplate}
              data-testid="bulk-file-template-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download template
            </Button>
            <span className="text-xs text-gray-500">
              One column: <code className="font-mono">entry_id</code> in the header row.
            </span>
          </div>

          <div className="border-2 border-dashed rounded-lg p-6 text-center bg-gray-50">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={onFileChange}
              data-testid="bulk-file-input"
              disabled={busy}
            />
            <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
            <div className="text-sm text-gray-700 mb-2">
              {fileName ? (
                <span className="font-mono">{fileName}</span>
              ) : (
                "Select an .xlsx or .csv file with a column of entry IDs."
              )}
            </div>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              data-testid="bulk-file-select-btn"
            >
              {busy ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing…</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" />Choose file</>
              )}
            </Button>
          </div>

          {malformedRows.length > 0 && (
            <Alert variant="destructive" data-testid="bulk-file-malformed-alert">
              <AlertTriangle className="w-4 h-4" />
              <AlertTitle>
                {malformedRows.length} malformed entry ID row{malformedRows.length === 1 ? "" : "s"} — file rejected
              </AlertTitle>
              <AlertDescription>
                <div className="text-xs mt-1">
                  Expected shape: <code className="font-mono">RMNE_YYYYMM_NNNNN</code>
                </div>
                <ul className="mt-2 max-h-40 overflow-y-auto space-y-1 text-xs font-mono">
                  {malformedRows.slice(0, 50).map((m) => (
                    <li key={m.row}>
                      Row {m.row}: <span className="text-red-700">{String(m.value).slice(0, 80)}</span>
                    </li>
                  ))}
                  {malformedRows.length > 50 && (
                    <li className="italic">… {malformedRows.length - 50} more</li>
                  )}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {alertPayload && (
            <Alert variant="destructive" data-testid="bulk-file-error-alert">
              <AlertTriangle className="w-4 h-4" />
              <AlertTitle>{alertPayload.title}</AlertTitle>
              <AlertDescription className="whitespace-pre-wrap text-sm">
                {alertPayload.detail}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} data-testid="bulk-file-close-btn">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
